import React, { useMemo } from "react";
import { MissionPayrollBreakdownFieldsFragment } from "../../../__generated__/operations/graphql";
import { useI18n } from "../../../hooks/i18n-hooks";
import { formatCurrency, minutesAsString } from "../../../utils";
import VStack from "../../../ui_atoms/VStack";
import { TitleMediumText } from "../../FormattedText";
import LabelValueText from "../../LabelValueText";

type AdditionGroup = {
    title: string;
    total: string;
    lines: Array<{ label: string; value: string }>;
};

type PayrollBreakdownAdditionsProps = {
    payrollBreakdown: MissionPayrollBreakdownFieldsFragment;
};

const formatAmount = (amountInCents: number) =>
    formatCurrency({ amountInCents, displayCurrencySymbol: false });

const formatHours = (minutes: number) => minutesAsString(minutes, { separator: "h" });

const amountGroup = (
    title: string,
    lines: Array<{ label: string; amountInCents: number }>,
): AdditionGroup => ({
    title,
    total: formatAmount(lines.reduce((total, line) => total + line.amountInCents, 0)),
    lines: lines.map((line) => ({ label: line.label, value: formatAmount(line.amountInCents) })),
});

/**
 * What the employer entered on the décompte besides the worked hours: the overtime it pays out,
 * auxiliary activities, bonuses, counted fees and deductions, effective fees. Read as saved, at
 * the amounts the worker is paid (or charged), group by group.
 */
const PayrollBreakdownAdditions: React.FC<PayrollBreakdownAdditionsProps> = ({
    payrollBreakdown,
}) => {
    const i18n = useI18n();

    const groups = useMemo(() => {
        const countLabel = (title: string, count: number, unitAmount: number) =>
            i18n.t("screens.payroll_breakdown.additions.count_line", {
                title,
                count,
                unitPrice: formatCurrency({ amountInCents: unitAmount }),
            });

        const result: AdditionGroup[] = [
            // the overtime cashed out, one line per pay rate, at what the payslip pays for it:
            // the standard pool at the CCT overtime rate (125% on some CCTs), a bucket at its own
            amountGroup(
                i18n.t("screens.payroll_breakdown.additions.overtime_paid"),
                (payrollBreakdown.overtimePaidAmounts || []).map((overtimePaid) => ({
                    label: i18n.t("screens.payroll_breakdown.additions.overtime_paid_at", {
                        hours: formatHours(overtimePaid.minutes),
                        percent: overtimePaid.payRatePercent,
                    }),
                    amountInCents: overtimePaid.amount,
                })),
            ),
            amountGroup(
                i18n.t("screens.payroll_breakdown_editor.product_categories.auxiliary_salary"),
                (payrollBreakdown.auxiliarySalaries || []).map((auxiliarySalary) => ({
                    label: `${auxiliarySalary.description} – ${formatHours(auxiliarySalary.duration)}`,
                    amountInCents: auxiliarySalary.grossAmount,
                })),
            ),
            amountGroup(
                i18n.t("screens.payroll_breakdown_editor.product_categories.bonus"),
                (payrollBreakdown.bonuses || []).map((bonus) => ({
                    label: bonus.description,
                    amountInCents: bonus.amount,
                })),
            ),
            amountGroup(
                i18n.t("screens.payroll_breakdown_editor.product_categories.variable_fee"),
                (payrollBreakdown.dynamicFees || [])
                    .filter((dynamicFee) => (dynamicFee.count || 0) > 0)
                    .map((dynamicFee) => ({
                        label: countLabel(
                            dynamicFee.fee.otherTitle ||
                                i18n.t(`fees.type.${dynamicFee.fee.type}.title`),
                            dynamicFee.count || 0,
                            dynamicFee.fee.amount,
                        ),
                        amountInCents: (dynamicFee.count || 0) * dynamicFee.fee.amount,
                    })),
            ),
            amountGroup(
                i18n.t("screens.payroll_breakdown_editor.product_categories.deduction"),
                (payrollBreakdown.dynamicDeductions || [])
                    .filter((dynamicDeduction) => (dynamicDeduction.count || 0) > 0)
                    .map((dynamicDeduction) => ({
                        label: countLabel(
                            dynamicDeduction.deduction.description,
                            dynamicDeduction.count || 0,
                            dynamicDeduction.deduction.amount,
                        ),
                        // a deduction is taken off the pay
                        amountInCents:
                            -(dynamicDeduction.count || 0) * dynamicDeduction.deduction.amount,
                    })),
            ),
            amountGroup(
                i18n.t("screens.payroll_breakdown_editor.product_categories.effective_fee"),
                (payrollBreakdown.effectiveFees || []).map((effectiveFee) => ({
                    label: effectiveFee.description,
                    amountInCents: effectiveFee.amount,
                })),
            ),
        ];
        return result.filter((group) => group.lines.length > 0);
    }, [i18n, payrollBreakdown]);

    if (groups.length === 0) {
        return null;
    }

    return (
        <VStack h="2/3" gap={1 / 2}>
            <TitleMediumText>{i18n.t("screens.payroll_breakdown.additions.title")}</TitleMediumText>
            {groups.map((group) => (
                <LabelValueText
                    key={group.title}
                    label={group.title}
                    value={group.total}
                    subValues={group.lines}
                />
            ))}
        </VStack>
    );
};

export default PayrollBreakdownAdditions;
